import { GraphQLFormattedError } from 'graphql';
import { property } from 'lodash';

export function formatGraphQLErrors(
  errors?: readonly GraphQLFormattedError[],
): string[] | undefined {
  // An empty array means "nothing to show", not "here are your messages". An
  // ApolloError raised by a network-layer failure carries `graphQLErrors: []`,
  // and returning that empty array (which is truthy) made every
  // `formatGraphQLErrors(...) ?? [fallback]` call site render nothing at all.
  // Returning undefined lets the caller's fallback message win.
  if (!errors?.length) {
    return undefined;
  }

  const result: string[] = [];

  errors.forEach((error) => {
    const fields = error.extensions?.exception?.validationErrors?.map(
      property('property'),
    );

    if (fields && fields.length) {
      result.push(`Bad input: ${fields.join(', ')}`);
    } else {
      result.push(error.message || 'Error');
    }
  });

  return result;
}

/** The API's code for a request blocked by a rate limit. */
export const RATE_LIMITED_CODE = 'auth:rate-limited';

function firstGraphQLError(err: unknown): GraphQLFormattedError | undefined {
  return (err as { graphQLErrors?: readonly GraphQLFormattedError[] } | null)
    ?.graphQLErrors?.[0];
}

/** The `extensions.code` of the first GraphQL error, such as an ApolloError's. */
export function graphQLErrorCode(err: unknown): string | undefined {
  const code = firstGraphQLError(err)?.extensions?.code;
  return typeof code === 'string' ? code : undefined;
}

/** The extensions of the first GraphQL error, such as `retryAfterSeconds`. */
export function graphQLErrorExtensions(err: unknown): Record<string, unknown> {
  return (firstGraphQLError(err)?.extensions ?? {}) as Record<string, unknown>;
}

/** The message of the first GraphQL error, or undefined for a network error. */
export function graphQLErrorMessage(err: unknown): string | undefined {
  const first = firstGraphQLError(err);
  return first ? formatGraphQLErrors([first])?.[0] : undefined;
}

/** "Too many attempts" with the wait from a RATE_LIMITED_CODE error. */
export function rateLimitMessage(err: unknown): string {
  const seconds = Number(graphQLErrorExtensions(err).retryAfterSeconds);

  if (!seconds || seconds <= 0) {
    return 'Too many attempts. Wait a few minutes, then try again.';
  }

  if (seconds < 60) {
    return 'Too many attempts. Wait a minute, then try again.';
  }

  const minutes = Math.ceil(seconds / 60);

  if (minutes >= 120) {
    return `Too many attempts. Try again in ${Math.ceil(minutes / 60)} hours.`;
  }

  return `Too many attempts. Try again in ${minutes} minute${
    minutes === 1 ? '' : 's'
  }.`;
}
